import FetchModule from '../../Model/Share/Network/FetchModule';

export const POLL_TIMEOUT_MS = 435000;

// 기존 Spring 인증·갱신 규칙으로 api3을 호출한다. 취소 신호는 갱신 뒤 재시도에도 유지한다. //
export async function requestTurn(path, method, body, signal) {
  const module = new FetchModule();
  const config = () => ({ ...(body === undefined ? module.getConfig(method) : module.postConfig(method, body)), signal });
  const url = `${module.baseURL}api3/ai-agent/run/${path}`;
  let response = await fetch(url, config());
  let result = await response.json();

  if (result.status === 401 && !signal.aborted) {
    await module.refreshAuthorization();
    if (signal.aborted) throw new Error('cancelled');

    response = await fetch(url, config());
    result = await response.json();
  }

  if (result.status === 403) module.handleForbidden();

  return result;
}

// 접수 이후 서버 턴은 계속된다. 취소·이전 질문의 응답은 화면에 전달하지 않는다. //
export async function runAcceptedTurn({ input, sessionId, turn, onProgress, request = requestTurn, now = Date.now }) {
  if (turn.cancelled) return null;

  const started = now();
  turn.deadlineTimer = setTimeout(() => {
    turn.timedOut = true;
    turn.controller.abort();
    clearTimeout(turn.pollTimer);
    turn.wake?.();
  }, POLL_TIMEOUT_MS);

  try {
    const accepted = await request('accept', 'POST', { input, sessionId }, turn.controller.signal);

    if (turn.cancelled) return null;
    if (accepted?.status === 409) {
      const error = new Error('turn already running');

      error.code = 'TURN_RUNNING';
      throw error;
    }
    if (accepted?.status !== 200 || !accepted?.data?.turnId) throw new Error(accepted?.message || 'accept failed');

    const turnId = accepted.data.turnId;

    while (!turn.cancelled && !turn.timedOut && now() - started < POLL_TIMEOUT_MS) {
      const response = await request(encodeURIComponent(turnId), 'GET', undefined, turn.controller.signal);

      if (turn.cancelled) return null;
      if (response?.status !== 200 || !response.data) throw new Error(response?.message || 'poll failed');

      const data = response.data;

      if (data.state === 'done' || data.state === 'failed') return data;
      if (data.state !== 'running') throw new Error('unknown turn state');

      onProgress(data.progressJson);
      await new Promise(resolve => {
        turn.wake = resolve;
        turn.pollTimer = setTimeout(resolve, now() - started < 15000 ? 1000 : 3000);
      });
      turn.wake = null;
      turn.pollTimer = null;
    }

    if (turn.cancelled) return null;
    throw new Error('poll timeout');
  }
  catch (error) {
    if (turn.timedOut) throw new Error('poll timeout');
    throw error;
  }
  finally { clearTimeout(turn.deadlineTimer); }
}

// 질문마다 만든 타이머와 요청을 해제한다. 새 질문 상태를 건드리지 않는다. //
export function stopTurn(turn) {
  if (!turn) return;

  turn.cancelled = true;
  turn.controller.abort();
  clearInterval(turn.loadingTimer);
  clearTimeout(turn.pollTimer);
  clearTimeout(turn.deadlineTimer);
  turn.wake?.();
}

// 비동기 저장 응답의 화면 채널을 복원한다. 구 턴은 평문으로 남긴다. //
export function decodeTurnRender(renderJson) {
  try {
    const parsed = JSON.parse(renderJson);

    if (!parsed || typeof parsed.text !== 'string') return {};

    return {
      text: parsed.text,
      ...(parsed.displayItems ? { displayItems: parsed.displayItems } : {}),
      ...(Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length ? { extraDisplays: parsed.extraDisplays } : {}),
      ...(parsed.vizData ? { vizData: parsed.vizData } : {}),
      ...(parsed.salesViewData?.weeksData ? { salesViewData: parsed.salesViewData } : {}),
      ...(parsed.revenueChartData ? { revenueChartData: parsed.revenueChartData } : {}),
      ...(parsed.smsRecommendation ? { smsRecommendation: parsed.smsRecommendation } : {}),
    };
  }
  catch {
    return {};
  }
}
